import { useMemo } from "react";

import dayjs from "dayjs";

import type { ActivationAlertsContractWebsiteContent } from "@similarweb/pro-api-client";
import { shortMonthDayAndYear } from "@similarweb/pro-logic-components";

import { useApiQuery } from "app/modules/api-client";

import { useCommonAlertsQueryParams } from "../useCommonAlertsQueryParams";

import { useWebsiteContentAlertsFilterStore } from "./filters/useWebsiteContentAlertsFilterStore";

export type WebsiteContentAlertItem = ActivationAlertsContractWebsiteContent & {
    formattedDate: string;
};

const matchesSearch = (item: ActivationAlertsContractWebsiteContent, searchValue: string): boolean => {
    if (!searchValue) {
        return true;
    }

    const lowerSearch = searchValue.toLowerCase();

    return (
        item.domain.toLowerCase().includes(lowerSearch) ||
        item.headline.toLowerCase().includes(lowerSearch) ||
        (item.content?.toLowerCase().includes(lowerSearch) ?? false)
    );
};

const select = (data: ActivationAlertsContractWebsiteContent[]): WebsiteContentAlertItem[] =>
    data.map((item) => ({
        ...item,
        formattedDate: dayjs(item.publishedDate).format(shortMonthDayAndYear),
    }));

export const useWebsiteContentAlertsQuery = () => {
    const searchValue = useWebsiteContentAlertsFilterStore((s) => s.searchValue);
    const params = useCommonAlertsQueryParams();

    const { data, ...rest } = useApiQuery(
        {
            tag: "Alerts",
            methodName: "activationAlertsWebsiteContentDetail",
            options: {
                select,
                refetchOnMount: true,
            },
        },
        params,
    );

    const filteredData = useMemo(() => data?.filter((item) => matchesSearch(item, searchValue)), [data, searchValue]);

    return { data: filteredData, ...rest };
};
